// Per-project repo-link cache (P3b).
// Loaded when a project settings panel opens; refreshed after every mutation.
// Mirrors the structure of store/automationRules.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import {
  getRepoLinks,
  createRepoLink as apiCreateRepoLink,
  deleteRepoLink as apiDeleteRepoLink,
  patchRepoLink as apiPatchRepoLink,
  getAvailableRepos,
  type ApiRepoLink,
  type AvailableRepo,
} from "../lib/api";

export interface RepoLinksState {
  loading: boolean;
  items: ApiRepoLink[];
  projectId: number | null;
  error: string | null;
}

const initial: RepoLinksState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};

const repoLinksSignal: Signal<RepoLinksState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadRepoLinks(projectId: number): Promise<void> {
  try {
    const items = await getRepoLinks(projectId);
    repoLinksSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    repoLinksSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    inFlight = null;
  }
}

/**
 * Hook: subscribe to the repo links for the given project.
 * Triggers a load when the projectId changes and guards against returning
 * a prior project's data while the new load is still in flight.
 */
export function useRepoLinks(projectId: number): RepoLinksState {
  useEffect(() => {
    const s = repoLinksSignal.value;
    if (!inFlight && (s.projectId !== projectId || s.error !== null)) {
      repoLinksSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadRepoLinks(projectId);
    }
  }, [projectId]);

  const state = repoLinksSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Force a fresh fetch — call after any mutation. */
export function refreshRepoLinks(projectId: number): Promise<void> {
  repoLinksSignal.value = { ...repoLinksSignal.value, loading: true };
  inFlight = loadRepoLinks(projectId);
  return inFlight;
}

// ---------- Available-repos fetch (one-off, not cached in a signal) ----------

/**
 * Fetch the list of repos that could be linked to this project.
 * Not cached — call when the add-picker is shown so data stays fresh.
 */
export async function fetchAvailableRepos(
  projectId: number
): Promise<AvailableRepo[]> {
  return getAvailableRepos(projectId);
}

// ---------- CRUD helpers ----------

/**
 * Link a repository to the project and refresh the cache.
 * Throws on error — caller is responsible for toasting (409 gets a custom
 * message, others fall through to the default handler).
 */
export async function addRepoLink(
  projectId: number,
  repositoryId: number
): Promise<void> {
  await apiCreateRepoLink(projectId, repositoryId);
  await refreshRepoLinks(projectId);
}

/**
 * Remove a repo link and update the cache optimistically.
 * Falls back to a full refresh if the delete fails.
 */
export async function removeRepoLink(
  projectId: number,
  linkId: number
): Promise<void> {
  // Optimistic removal
  const cur = repoLinksSignal.value;
  if (cur.projectId === projectId) {
    repoLinksSignal.value = {
      ...cur,
      items: cur.items.filter((l) => l.id !== linkId),
    };
  }
  try {
    await apiDeleteRepoLink(projectId, linkId);
  } catch (err) {
    // Revert on failure
    await refreshRepoLinks(projectId);
    throw err;
  }
}

/**
 * Toggle ai_automation_enabled on a repo link and refresh the cache.
 * Throws on error so the component can show the correct toast.
 */
export async function toggleRepoLinkAi(
  projectId: number,
  linkId: number,
  enabled: boolean
): Promise<void> {
  await apiPatchRepoLink(projectId, linkId, { ai_automation_enabled: enabled });
  await refreshRepoLinks(projectId);
}

/**
 * Toggle create_from_git_enabled (the git→juuplan import direction) on a repo
 * link and refresh the cache. Throws on error so the component can toast.
 */
export async function toggleRepoLinkCreateFromGit(
  projectId: number,
  linkId: number,
  enabled: boolean
): Promise<void> {
  await apiPatchRepoLink(projectId, linkId, { create_from_git_enabled: enabled });
  await refreshRepoLinks(projectId);
}
